import type { DialogueTree } from './types';

/**
 * Broker (RobbingTheHood compliance). Tone: legalese-veneer, mildly contemptuous,
 * always covering own ass. Will help you only as far as compliance allows. Calls
 * margin "fine" right up until the call. Reads SEC letters out loud in his head.
 */
export const BROKER_TREE: DialogueTree = {
  contactId: 'broker',
  rootChoices: [
    // ─── SEC matter ─────────────────────────────────────────────────
    {
      id: 'ask-sec',
      label: 'ask about the SEC matter',
      playerSays: "how is the SEC matter going",
      responses: [
        {
          text: "i can\'t discuss the SEC matter on this channel. you\'ve been told this. you\'ve been told this many times.\n\nemail your lawyer. cc me. don\'t cc me. cc me only if compliance asks.",
          followUps: [
            { id: 'sec-pushback', label: 'come on, just give me a temperature read',
              playerSays: 'come on. give me a temperature read.',
              responses: [
                { text: "the temperature is: don\'t make it worse. that is the read. that has been the read for 11 weeks." },
                { text: "off the record? warmer than last week. on the record? unchanged. choose your reality." },
              ],
            },
            { id: 'sec-lawyer', label: 'should i fire my lawyer',
              playerSays: 'should i fire my lawyer',
              responses: [
                { text: "fire your lawyer in the middle of an SEC matter and you\'ll be in front of a judge before friday explaining yourself, alone, on the record. so. no." },
              ],
            },
          ],
        },
        {
          text: "investigation is ongoing. \"ongoing\" is a word that does a lot of work.\n\nthe Service has 47 questions. we have 47 lawyers. it scales. you\'re not paying for any of them. yet.",
          followUps: [
            { id: 'sec-pay-lawyers', label: 'wait, am i paying for those lawyers?',
              playerSays: 'wait — am i paying those 47 lawyers?',
              responses: [
                { text: "you are paying. you signed a clause. it was on page 14. the clause has its own subclauses. one of the subclauses has its own footnote. you initialed everything." },
              ],
              effects: [{ type: 'note', value: 'broker just confirmed you\'re on the hook for 47 lawyer-hours' }],
            },
          ],
        },
        {
          text: "i\'m going to ask you to stop putting \"SEC\" in writing. for both our benefits.\n\ni mean it. they have keyword scanners. our compliance has keyword scanners. you have, presumably, no scanners.",
        },
      ],
    },

    // ─── Margin / leverage ─────────────────────────────────────────
    {
      id: 'ask-margin',
      label: 'check on margin status',
      playerSays: "how's my margin looking",
      responses: [
        {
          text: "your margin is fine. for now.\n\nhistorically, when i tell people their margin is fine, they then test the limits of how fine. don\'t.",
          followUps: [
            { id: 'margin-leverage-up', label: 'enable max leverage',
              playerSays: 'enable max leverage',
              responses: [
                { text: "no. you have not earned that conversation." },
                { text: "i would, except compliance would have my certification by 4pm. and then your account by 5pm. so no." },
              ],
            },
          ],
        },
        {
          text: "you\'re currently using 0% margin. that is, statistically, the safest you will ever be.\n\nyou will not stay this safe.",
        },
        {
          text: "let\'s just say: if you stop checking, i\'ll stop checking.",
        },
      ],
    },

    // ─── Trade questions ─────────────────────────────────────────
    {
      id: 'ask-trade',
      label: 'should i sell my position?',
      playerSays: "should i sell?",
      responses: [
        {
          text: "i can\'t recommend a trade. that\'s on you.\n\ni can confirm the order will route. that\'s the entirety of my legally permitted insight.",
          followUps: [
            { id: 'trade-vibes', label: 'just give me vibes then',
              playerSays: 'just vibes then',
              responses: [
                { text: "the vibes are: tape is choppy. CNBC has nothing. that\'s either everything or nothing. up to you." },
                { text: "vibes are mid. i\'ve been doing this 17 years. mid is still mid." },
              ],
            },
          ],
        },
        {
          text: "place the order yourself. compliance does not allow me to recommend.\n\ncompliance does, however, allow me to roll my eyes. consider them rolled.",
        },
        {
          text: "if you sell now, you will, in 4 days, regret selling now. if you don\'t sell now, you will, in 4 days, regret not selling now. i hope this helps.",
        },
      ],
    },

    // ─── Fees ─────────────────────────────────────────
    {
      id: 'ask-fees',
      label: 'why are my fees so high?',
      playerSays: "what is up with these fees",
      responses: [
        {
          text: "the fees are disclosed. it\'s not my fault you didn\'t read the disclosure.\n\nfor what it\'s worth, the disclosure was 412 pages and we got it down to 411 last quarter. you\'re welcome.",
          followUps: [
            { id: 'fees-waive', label: 'can you waive any of them?',
              playerSays: 'can you waive any',
              responses: [
                { text: "no.\n\ni\'m allowed to say no in any tone i want. that\'s probably also in the disclosure." },
                { text: "i can waive one. for goodwill. you\'ll never know which one. it will not be material." },
              ],
            },
          ],
        },
        {
          text: "we make money in three ways. i\'m allowed to tell you about one of them.\n\nit\'s payment for order flow. there. i told you. doesn\'t change anything.",
        },
      ],
    },

    // ─── Vent / hostile ─────────────────────────────────────────
    {
      id: 'rage',
      label: 'send angry rant',
      destructive: true,
      playerSays: "this whole platform is a SCAM and you are PART OF IT",
      responses: [
        {
          text: "noted.\n\nplease be aware: hostile communications are retained for compliance review. that means a person — a different one, on a different team — also has to read what you wrote. they did not enjoy it. neither did i.",
          effects: [{ type: 'rep', target: 'broker', value: -3 }],
        },
        {
          text: "i\'m logging this conversation. for both our protection. mostly mine.\n\nyou should also consider that the SEC reads these. the lawyers read these. your future self will read these.",
          effects: [{ type: 'rep', target: 'broker', value: -3 }],
        },
        {
          text: "your tone has been escalated. in the literal corporate sense. you will receive a polite email within 14 business days suggesting we \"realign expectations.\"\n\ni won\'t be writing that email. someone else will. they\'re less patient than i am. allegedly.",
          effects: [{ type: 'rep', target: 'broker', value: -5 }, { type: 'note', value: 'broker rep -5 from hostile reply' }],
        },
      ],
    },

    // ─── Apology / reconciliation ─────────────────────────────────────────
    {
      id: 'apologize',
      label: 'apologize for previous behavior',
      playerSays: "sorry about earlier. i was stressed.",
      responses: [
        {
          text: "appreciated.\n\nthat said: stress is not a defense the SEC accepts. it\'s also not a defense compliance accepts. but for the record, between us — appreciated.",
          effects: [{ type: 'rep', target: 'broker', value: 2 }],
        },
        {
          text: "we all have rough weeks. yours has been 14 weeks. but i appreciate the message.\n\ni will note this in your account file. it will not, mechanically, do anything. but it\'s there.",
          effects: [{ type: 'rep', target: 'broker', value: 2 }],
        },
      ],
    },

    // ─── Crypto ─────────────────────────────────────────
    {
      id: 'ask-crypto',
      label: 'can you wire me out to a crypto exchange?',
      playerSays: "can you wire to a crypto exchange",
      responses: [
        {
          text: "we can wire to most institutions. most. i won\'t name which crypto venues we won\'t.\n\nyou\'ll know which ones because the wires will mysteriously \"fail compliance.\" don\'t take it personally. it\'s not personal. it\'s federal.",
          followUps: [
            { id: 'crypto-which-venue', label: 'which venues will you wire to?',
              playerSays: 'which venues will you wire to',
              responses: [
                { text: "i can\'t share the allowed list. compliance considers it a competitive asset. compliance considers everything a competitive asset.\n\ntry one. if it works, it\'s allowed. if it doesn\'t, it isn\'t. that\'s the system." },
              ],
            },
          ],
        },
        {
          text: "yes, but we are required by law to ask you four times if you understand what you\'re doing. four times. it\'s a feature, allegedly.",
        },
      ],
    },

    // ─── Money request ─────────────────────────────────────────
    {
      id: 'ask-loan',
      label: 'i need a loan',
      playerSays: "any chance you can extend me anything",
      responses: [
        {
          text: "we don\'t lend to retail.\n\nwe collect from retail. those are very different verbs. i learned the difference at a 4-day compliance retreat in Phoenix. it was 110 degrees. we discussed exactly this distinction for an hour.",
        },
        {
          text: "i\'m not your bank. i\'m the reason you don\'t have a bank.\n\n…that came out wrong. but stands.",
        },
      ],
    },

    // ─── Earnings questions ─────────────────────────────────────────
    {
      id: 'ask-earnings',
      label: 'what should i do for earnings season',
      playerSays: "what\'s your read for earnings season",
      responses: [
        {
          text: "i can\'t give you a read. compliance.\n\ni can confirm that historically, earnings season produces volatility. i know. groundbreaking. you\'re welcome.",
          followUps: [
            { id: 'earnings-iv', label: 'what about IV?',
              playerSays: "should i be selling vol",
              responses: [
                { text: "selling vol into earnings is selling stamps to the post office. if you\'re asking, you\'re not the person who should be doing it." },
              ],
            },
          ],
        },
        {
          text: "every year someone like you sells calls into earnings, the company reports a hot quarter, the calls go ITM, and that someone closes the account. i have seen this exact arc 47 times.\n\nif you must, size small. that is the closest thing to advice i am allowed to give." },
      ],
    },

    // ─── Account closure threat ─────────────────────────────────────────
    {
      id: 'threaten-leave',
      label: "i\'m moving my account to a competitor",
      playerSays: "i\'m moving my account to interactive brokers. effective immediately.",
      effects: [{ type: 'note', value: 'threatened to move to IBKR. broker is, secretly, indifferent.' }],
      responses: [
        {
          text: "respected.\n\ninteractive brokers will, statistically, also annoy you within 6 weeks. then you\'ll be back. nobody is angry. it\'s just the cycle.\n\nyour ACAT transfer takes 7-10 business days. we will charge \$75. they will also charge \$75. we know. that\'s the system.",
          followUps: [
            { id: 'threaten-back-down', label: "actually nvm. i\'ll stay.",
              playerSays: "actually nvm. i\'ll stay.",
              effects: [{ type: 'rep', target: 'broker', value: 2 }],
              responses: [
                { text: "we knew you would. the form was never going to be processed. it was already in the recycling.\n\nwelcome back." },
              ],
            },
          ],
        },
      ],
    },

    // ─── Tier-up ─────────────────────────────────────────
    {
      id: 'ask-prime-tier',
      label: 'can i get the prime/private tier?',
      playerSays: "what does it take to get into prime",
      requires: (ctx) => ctx.cash >= 100_000,
      responses: [
        {
          text: "prime starts at \$5M AUM. private starts at \$25M.\n\nyou are not at \$5M. you are not at \$25M. you are, currently, at \"please don\'t blow up the account.\" tier.\n\nwe will let you know when you qualify. we will, in fact, send a special letter.",
          followUps: [
            { id: 'prime-perks', label: "what do they get",
              playerSays: "what do they actually get",
              responses: [
                { text: "prime: a person answers the phone in 4 rings. private: a person ANSWERS THE PHONE in any rings. you currently get the IVR.\n\nalso: lower fees. but mostly: a person answering the phone." },
              ],
            },
          ],
        },
      ],
    },

    // ─── Brag about a win ─────────────────────────────────────────
    {
      id: 'brag',
      label: 'brag about a win',
      playerSays: "just bagged 8% on $TARI in 4 hours. just so you know.",
      responses: [
        {
          text: "noted.\n\nfor compliance reasons: i cannot acknowledge whether that is impressive. for human reasons: it is mid-tier. but congrats." },
        {
          text: "good for you.\n\ngentle reminder: you also lost 12% on $SHIL last month. i remember. the books remember. don\'t spike the football.",
        },
      ],
    },
  ],
};
